/**
 * audioUpload.js — Upload audio chunks to GCS and request transcription.
 *
 * Supports two modes:
 * 1. Rolling (per-chunk): uploadSingleChunk + transcribeSingleChunk during recording
 * 2. Batch (legacy): uploadAllChunks after recording stops
 *
 * See: docs/features/document-system/audio-quality-gates.md (Phase 1)
 * See: docs/features/integrations/deepgram-prerecorded.md
 */

import { ServerURI, preRecordedMaxRetries } from "../globals";
import { auth } from "./firebase";

export const MIN_AUDIO_CHUNK_BYTES = 64;

export function isUsableAudioBlob(blob) {
  return Boolean(blob && Number.isFinite(blob.size) && blob.size >= MIN_AUDIO_CHUNK_BYTES);
}

function assertUsableAudioChunk(chunk) {
  if (!chunk || !isUsableAudioBlob(chunk.blob)) {
    const size = chunk?.blob?.size ?? 0;
    throw new Error(`Audio chunk ${chunk?.index ?? 'unknown'} is empty or unusable (${size} bytes)`);
  }
}

/**
 * Upload a single audio chunk to GCS with retry.
 * Used during rolling transcription — called as each chunk completes.
 * @param {{ blob: Blob, index: number }} chunk - Single chunk from AudioChunkRecorder
 * @param {string} documentId - Firestore document ID
 * @returns {Promise<{ gcsUri: string, chunkIndex: number }>}
 */
export async function uploadSingleChunk(chunk, documentId) {
  // Validate outside the retry loop: retrying the same malformed blob cannot help.
  assertUsableAudioChunk(chunk);
  let lastError = null;

  for (let attempt = 0; attempt < preRecordedMaxRetries; attempt++) {
    try {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
      }

      const token = await auth.currentUser?.getIdToken();
      const formData = new FormData();
      formData.append('chunk', chunk.blob, `chunk-${chunk.index}.webm`);
      formData.append('documentId', documentId);
      formData.append('chunkIndex', String(chunk.index));

      const response = await fetch(`${ServerURI}/api/upload-audio-chunk`, {
        method: 'POST',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return { gcsUri: data.gcsUri, chunkIndex: chunk.index };
    } catch (err) {
      lastError = err;
      console.warn(`[audioUpload] Chunk ${chunk.index} upload attempt ${attempt + 1} failed:`, err.message);
    }
  }

  throw new Error(`Failed to upload chunk ${chunk.index} after ${preRecordedMaxRetries} attempts: ${lastError.message}`);
}

/**
 * Transcribe a single uploaded chunk via the backend.
 * Reuses /api/transcribe-chunks with a single-element chunkUris array.
 * Backend skips dedup for single chunks and returns per-chunk quality data.
 * @param {object} params
 * @param {string} params.documentId
 * @param {string} params.patientId
 * @param {string} params.chunkUri - Single GCS URI
 * @param {number} params.chunkIndex
 * @returns {Promise<{ transcript: string, wordCount: number, speakerCount: number, quality?: object }>}
 */
export async function transcribeSingleChunk({ documentId, patientId, chunkUri, chunkIndex }) {
  const token = await auth.currentUser?.getIdToken();
  const response = await fetch(`${ServerURI}/api/transcribe-chunks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    body: JSON.stringify({
      documentId,
      patientId,
      chunkUris: [chunkUri],
      chunkIndex,
      singleChunk: true,  // Signal to backend: save per-chunk data, skip GCS delete
    }),
  });

  if (!response.ok) {
    let detail = '';
    try {
      const errorJson = await response.json();
      detail = errorJson.detail || errorJson.error || JSON.stringify(errorJson);
    } catch {
      detail = await response.text();
    }
    throw new Error(`Single-chunk transcription failed: ${response.status} — ${detail}`);
  }

  return response.json();
}

/**
 * Request final assembly of pre-transcribed chunks (concatenation with dedup).
 * Calls /api/transcribe-chunks with preTranscribed flag — backend reads per-chunk
 * data from Firestore instead of re-calling Deepgram.
 * @param {object} params
 * @param {string} params.documentId
 * @param {string} params.patientId
 * @param {string[]} params.chunkUris - All GCS URIs in order
 * @param {string} params.streamSessionId
 * @param {number} params.totalChunks - Number of chunks to assemble
 * @returns {Promise<{ transcript: string, wordCount: number, speakerCount: number, quality?: object }>}
 */
export async function requestFinalAssembly({ documentId, patientId, chunkUris, streamSessionId, totalChunks }) {
  const token = await auth.currentUser?.getIdToken();
  const response = await fetch(`${ServerURI}/api/transcribe-chunks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    body: JSON.stringify({
      documentId,
      patientId,
      chunkUris,
      streamSessionId,
      preTranscribed: true,
      totalChunks,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Final assembly failed: ${response.status} ${errorText}`);
  }

  return response.json();
}

/**
 * Upload all audio chunks to GCS sequentially.
 * @param {Array<{ blob: Blob, index: number }>} chunks - Ordered chunks from AudioChunkRecorder
 * @param {string} documentId - Firestore document ID
 * @param {function} onProgress - Called with (completedCount, totalCount)
 * @returns {Promise<string[]>} Array of GCS URIs in order
 */
export async function uploadAllChunks(chunks, documentId, onProgress) {
  const gcsUris = [];
  const usableChunks = (chunks || []).filter((chunk) => {
    if (chunk && isUsableAudioBlob(chunk.blob)) return true;
    console.warn(`[audioUpload] Skipping unusable chunk ${chunk?.index ?? 'unknown'} (${chunk?.blob?.size ?? 0} bytes)`);
    return false;
  });

  if (usableChunks.length === 0) {
    throw new Error('No usable audio chunks were captured');
  }

  for (let i = 0; i < usableChunks.length; i++) {
    const chunk = usableChunks[i];
    let lastError = null;

    for (let attempt = 0; attempt < preRecordedMaxRetries; attempt++) {
      try {
        if (attempt > 0) {
          // Exponential backoff: 1s, 2s, 4s
          await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
        }

          const token = await auth.currentUser?.getIdToken();
        const formData = new FormData();
        formData.append('chunk', chunk.blob, `chunk-${chunk.index}.webm`);
        formData.append('documentId', documentId);
        formData.append('chunkIndex', String(chunk.index));

        const response = await fetch(`${ServerURI}/api/upload-audio-chunk`, {
          method: 'POST',
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        gcsUris.push(data.gcsUri);
        lastError = null;
        break; // Success
      } catch (err) {
        lastError = err;
        console.warn(`[audioUpload] Chunk ${chunk.index} upload attempt ${attempt + 1} failed:`, err.message);
      }
    }

    if (lastError) {
      throw new Error(`Failed to upload chunk ${chunk.index} after ${preRecordedMaxRetries} attempts: ${lastError.message}`);
    }

    if (onProgress) {
      onProgress(i + 1, usableChunks.length);
    }
  }

  return gcsUris;
}

/**
 * Request server-side transcription of uploaded chunks.
 * @param {object} params
 * @param {string} params.documentId
 * @param {string} params.patientId
 * @param {string[]} params.chunkUris - GCS URIs in order
 * @param {string} params.streamSessionId
 * @returns {Promise<{ transcript: string, wordCount: number, speakerCount: number }>}
 */
export async function requestTranscription({ documentId, patientId, chunkUris, streamSessionId }) {
  const token = await auth.currentUser?.getIdToken();
  const response = await fetch(`${ServerURI}/api/transcribe-chunks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    body: JSON.stringify({ documentId, patientId, chunkUris, streamSessionId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Transcription request failed: ${response.status} ${errorText}`);
  }

  return response.json();
}
